import { AgoraClient, AgoraEventHandler } from "@/agora";
import { store } from "@/store";
import { RemoteTeachingService } from "@/services";
import {
  RealtimeClient,
  GLSocketReconnectContext,
  createRealtimeTransportOptions,
  resolveChannelName,
  SessionRealtimeChannelKey,
  DEFAULT_SESSION_CHANNEL_KEY,
  REALTIME_CHANNEL,
} from "@/ws";
import { ReconnectCoordinator, ReconnectContext } from "@/ws/reconnect-coordinator";
import { BaseRoomManager, RoomOptions } from "./base.manager";
import { PresenceData, RoomModel, SessionRealtimeChannelsModel, UserRole } from "@/models";
import { LoginInfo, RoleName } from "vue-glcommonui";
import { HelperState, TeacherState } from "@/store/room/interface";
import { isUserPublishedAudio, isUserPublishedVideo } from "@/store/calling/utils";
import { MODULES, vuexName } from "@/store/utils";
import { notifySameUserJoined } from "@/utils/notifications";
import router from "@/router";
import { Paths } from "@/utils/paths";

export class TeacherRoomManager extends BaseRoomManager<RealtimeClient> {
  private activeRealtimeChannelKeys: SessionRealtimeChannelKey[] = [DEFAULT_SESSION_CHANNEL_KEY, REALTIME_CHANNEL.TEACHER_HELPER];
  private hasHandledKickOut = false;
  private readonly coordinator: ReconnectCoordinator;

  constructor(options: RoomOptions) {
    super();
    this.options = options;
    if (options.agora) {
      this.agoraClient = new AgoraClient(options.agora);
    }
    const realtimeOptions = this.buildTeacherRealtimeOptions();
    if (!realtimeOptions) {
      throw new Error("Failed to create realtime transport options for RealtimeClient");
    }

    // Create per-manager ReconnectCoordinator instance (not global singleton)
    this.coordinator = new ReconnectCoordinator("teacher");
    this.coordinator.setResyncCallback(async (context: ReconnectContext) => {
      await this.runResyncPipeline(context);
    });

    this.WSClient = new RealtimeClient({
      url: `${process.env.VUE_APP_REMOTE_TEACHING_SERVICE}/${process.env.VUE_APP_REMOTE_TEACHING_HUB}`,
      realtime: realtimeOptions,
      coordinator: this.coordinator,
      // Note: reConnectedCallback is now handled by ReconnectCoordinator via handleConnectionStateChange
      // This callback is kept for backward compatibility but coordinator decides when to run resync
      reConnectedCallback: async (_newConnectionId: string | undefined, _context: GLSocketReconnectContext) => {
        // Coordinator handles this via handleReconnection() in RealtimeClient
        // No action needed here - resync is triggered via coordinator.setResyncCallback
      },
    });
    this.WSClient.init();
  }

  /**
   * Resync pipeline called by ReconnectCoordinator when snapshot resync is needed
   * This is the single place where all reconnection state refresh happens
   *
   * Pipeline order:
   * 1. ENTER_CLASS(true) - re-enter on server
   * 2. Fetch snapshot (INIT_CLASS_ROOM)
   * 3. switchChannels (only if channel keys changed, conditional forceSpaceUpdate)
   * 4. ensureReadyAfterReconnect (subscribe + attach)
   */
  private async runResyncPipeline(_context: ReconnectContext) {
    store.commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_IS_REALTIME_READY), false);

    // Step 1: Re-enter class (server-side)
    await store.dispatch(vuexName(MODULES.TEACHER_ROOM.DISPATCHES.ENTER_CLASS), true);

    // Step 2: Fetch fresh snapshot from server
    const refreshSessionData = async () => {
      const { getters, dispatch } = store;
      const room = getters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_INFO)] as RoomModel;
      const { groupId, deviceId } = getters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_PARAMS_TO_JOIN_CUR_SESSION)];
      const loginInfo: LoginInfo = getters[vuexName(MODULES.AUTH.GETTERS.GET_LOGIN_INFO)];
      const isHelper = store.getters[vuexName(MODULES.TEACHER.GETTERS.GET_IS_HELPER)];
      await dispatch(vuexName(MODULES.TEACHER_ROOM.DISPATCHES.INIT_CLASS_ROOM), {
        classId: room.classInfo.classId,
        userId: loginInfo.profile.sub,
        userName: loginInfo.profile.name,
        role: RoleName.teacher,
        deviceId,
        isHelper,
        groupId,
        callFirstTime: false,
      });
    };
    await refreshSessionData();

    // Step 3: Switch channels (only if keys changed)
    // Determine if we need force space update (only when channel keys actually change)
    const currentKeys = this.activeRealtimeChannelKeys;
    const newKeys = this.resolveActiveRealtimeChannelKeys();
    const keysChanged = JSON.stringify([...currentKeys].sort()) !== JSON.stringify([...newKeys].sort());
    await this.switchChannels(undefined, keysChanged);

    // Step 4: Ensure channels are ready (subscribe + attach, no detach)
    await this.WSClient.ensureReadyAfterReconnect();

    store.commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_IS_REALTIME_READY), true);
  }
  /**
   * @deprecated Use runResyncPipeline via ReconnectCoordinator instead
   * Kept for backward compatibility
   */
  async handleSuccessfulReconnection(newConnectionId: string | undefined, _context: GLSocketReconnectContext) {
    // Forward to coordinator for proper handling
    await this.coordinator.forceResync({
      previousState: "unknown",
      lastOfflineState: "disconnected",
      offlineDurationMs: 0,
      connectionId: newConnectionId,
    });
  }

  async join(options: { classId?: string; studentId?: string; teacherId?: string; idOne?: string; callingEventHandlers: AgoraEventHandler | null }) {
    if (!options.teacherId || !options.classId) throw new Error("Missing Params");
    await this.WSClient.connect();
    await this.agoraClient.joinRTCRoom({ ...options, videoEncoderConfigurationPreset: "480p" }, false, async () => {
      await this.reconnectAgoraRoom();
    });

    // Sync channels will automatically enter presence
    await this.switchChannels();

    // Register realtime handlers via ENTER_CLASS
    // This must be called AFTER switchChannels() so handlers are subscribed to the attached channels
    await store.dispatch(vuexName(MODULES.TEACHER_ROOM.DISPATCHES.ENTER_CLASS), false);
  }

  async reconnectAgoraRoom() {
    if (!this.agoraClient.joinRoomOptions) return;
    const joinAsHelper = store.getters[vuexName(MODULES.TEACHER.GETTERS.GET_IS_HELPER)];
    const deviceStatus = {
      camera: false,
      microphone: false,
    };
    if (joinAsHelper) {
      const helper: HelperState | undefined = store.getters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_HELPER_INFO)];
      const isVideoHelperPublished = isUserPublishedVideo({ userId: helper?.id ?? "", gettersOrRootGetters: store.getters });
      const isAudioHelperPublished = isUserPublishedVideo({ userId: helper?.id ?? "", gettersOrRootGetters: store.getters });
      deviceStatus.camera = isVideoHelperPublished ?? false;
      deviceStatus.microphone = isAudioHelperPublished ?? false;
    } else {
      const teacher: TeacherState | undefined = store.getters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_TEACHER)];
      const isVideoTeacherPublished = isUserPublishedVideo({ userId: teacher?.id ?? "", gettersOrRootGetters: store.getters });
      const isAudioTeacherPublished = isUserPublishedAudio({ userId: teacher?.id ?? "", gettersOrRootGetters: store.getters });
      deviceStatus.camera = isVideoTeacherPublished ?? false;
      deviceStatus.microphone = isAudioTeacherPublished ?? false;
    }
    const options = { ...this.agoraClient.joinRoomOptions, camera: deviceStatus.camera, microphone: deviceStatus.microphone };
    await this.agoraClient.joinRTCRoom(options, true);
  }

  async manualReconnectAgoraRoom() {
    await this.reconnectAgoraRoom();
  }

  async close(end?: boolean) {
    await store.dispatch(vuexName(MODULES.SPACE_PRESENCE.DISPATCHES.DISCONNECT));
    await this.WSClient.disconnect();
    if (end) {
      await this.agoraClient.reset();
    } else {
      await this.agoraClient.leaveChannel();
    }
  }

  async handleKickOut() {
    if (this.hasHandledKickOut) return;

    this.hasHandledKickOut = true;

    await store.dispatch(vuexName(MODULES.TEACHER_ROOM.DISPATCHES.RESET_CLASS, false), false);
    notifySameUserJoined();
    try {
      await router.push(Paths.Teacher);
    } catch (error) {
      //
    }
  }

  private buildTeacherRealtimeOptions() {
    const clientId = this.resolveRealtimeClientId();
    const channelsConfig = store.getters[vuexName(MODULES.REALTIME_SETTINGS.GETTERS.GET_SETTINGS)] as SessionRealtimeChannelsModel | undefined;
    return createRealtimeTransportOptions({
      channelResolver: () => this.composeRealtimeChannelNames(),
      clientId,
      tokenProvider: clientId ? () => this.fetchRealtimeToken(clientId) : undefined,
      channelsConfig,
    });
  }

  private composeRealtimeChannelNames(keys: SessionRealtimeChannelKey[] = this.activeRealtimeChannelKeys): string[] | undefined {
    const channels = store.getters[vuexName(MODULES.REALTIME_SETTINGS.GETTERS.GET_SETTINGS)] as SessionRealtimeChannelsModel | null | undefined;
    const resolved = keys.map((key) => resolveChannelName(key, channels)).filter((name): name is string => !!name);
    return resolved.length ? Array.from(new Set(resolved)) : undefined;
  }

  private resolveRealtimeClientId(): string | undefined {
    return store.getters[vuexName(MODULES.AUTH.GETTERS.GET_LOGIN_INFO)]?.profile?.sub;
  }

  private async fetchRealtimeToken(userId?: string) {
    const { data } = await RemoteTeachingService.fetchRealtimeToken(userId);
    return data;
  }

  private resolveActiveRealtimeChannelKeys(): SessionRealtimeChannelKey[] {
    const isHelper = store.getters[vuexName(MODULES.TEACHER.GETTERS.GET_IS_HELPER)];
    const oneOneId = store.getters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_STUDENT_MODE_ONE_ID)] as string;

    if (oneOneId) {
      return isHelper ? [REALTIME_CHANNEL.HELPER_ONE_ONE] : [REALTIME_CHANNEL.TEACHER_ONE_ONE];
    }
    return [DEFAULT_SESSION_CHANNEL_KEY, REALTIME_CHANNEL.TEACHER_HELPER];
  }

  private resolveSpacePresenceData(): PresenceData | undefined {
    const loginInfo: LoginInfo | undefined = store.getters[vuexName(MODULES.AUTH.GETTERS.GET_LOGIN_INFO)];
    const isHelper = store.getters[vuexName(MODULES.TEACHER.GETTERS.GET_IS_HELPER)];

    if (!loginInfo?.profile?.sub) return undefined;

    return {
      userId: loginInfo.profile.sub,
      role: isHelper ? UserRole.HELPER : UserRole.TEACHER,
      name: loginInfo.profile.name,
    };
  }

  private resolveSpaceChannelName(channelKeys: SessionRealtimeChannelKey[]) {
    const channels = store.getters[vuexName(MODULES.REALTIME_SETTINGS.GETTERS.GET_SETTINGS)] as SessionRealtimeChannelsModel | null | undefined;
    const prioritizedKey =
      channelKeys.find((key) => key === REALTIME_CHANNEL.TEACHER_ONE_ONE || key === REALTIME_CHANNEL.HELPER_ONE_ONE) ?? DEFAULT_SESSION_CHANNEL_KEY;

    return resolveChannelName(prioritizedKey, channels ?? undefined);
  }

  private async syncSpacePresence(channelKeys: SessionRealtimeChannelKey[], forceSpaceUpdate: boolean) {
    const presenceData = this.resolveSpacePresenceData();
    if (!presenceData) return;

    await store.dispatch(vuexName(MODULES.SPACE_PRESENCE.DISPATCHES.INIT), {
      getRealtime: () => this.WSClient.getRealtimeTransport()?.getRealtimeClient(),
      profileData: presenceData,
      enableSingleTab: true,
      onKickOut: async () => {
        await this.handleKickOut();
      },
    });

    // Update space to the appropriate channel (handles channel switching)
    // Note: Space enter/leave automatically broadcasts presence updates to other members
    const channelName = this.resolveSpaceChannelName(channelKeys);
    await store.dispatch(vuexName(MODULES.SPACE_PRESENCE.DISPATCHES.UPDATE_SPACE), {
      channelName,
      force: forceSpaceUpdate,
    });
  }

  async switchChannels(keys?: SessionRealtimeChannelKey[], forceSpaceUpdate = false) {
    const newChannelKeys = Array.from(new Set(keys ?? this.resolveActiveRealtimeChannelKeys()));

    this.activeRealtimeChannelKeys = newChannelKeys;
    const channelNames = this.composeRealtimeChannelNames();
    if (channelNames?.length) {
      await this.WSClient.setRealtimeChannels(channelNames);
    }

    await this.syncSpacePresence(newChannelKeys, forceSpaceUpdate);
  }
}
